import type { Urls } from 'Common/Urls';
import { boundImageFieldPath, boundImageVarName, resolveThemeVar, type ThemeRecord } from '../../project/themeVariables';
import { fieldImageUrl } from './fieldImageUrl';
import type { RuntimeImage, DataRecord, UploadedImageData } from '../../types';

/*
	Resolves an image object's display URL from its upload/field/binding state, against the given
	record. Returns null when the object has nothing to show — the canvas renders its placeholder,
	and the buildable check (see project/buildable) reads that as a missing image.

	A free function rather than a method on ImageImpl: the impls capture the record at mount, so
	they can't answer for a record the caller supplies (cf anyTemplateContentCropped).
*/
export function effectiveImageSrc(
	obj: RuntimeImage,
	record: DataRecord,
	theme: ThemeRecord | undefined,
	urls: Pick<Urls,'imageUrl'>,
	proxyProductionAssets: boolean,
): string | null
{
	if (!obj.isField) {
		// Upload source: a theme-variable binding on the image source is the design
		// intent and overrides the uploaded/static image, resolving the variable's
		// value (string URL or stored image value) from the theme lookup.
		const varName = boundImageVarName(obj);
		if (varName) {
			const val = resolveThemeVar(theme, varName);
			return fieldImageUrl(boundImageFieldPath(obj)!, val, urls, proxyProductionAssets);
		}

		if (obj.image)
			return uploadedImageUrl(obj.image, urls, proxyProductionAssets);
		return obj.src ?? null;
	}

	// Field source: a user-uploaded image overrides the field source, breaking the link
	// to the record's field data. Undo (or Reset) drops the upload and restores the link.
	if (obj.image)
		return uploadedImageUrl(obj.image, urls, proxyProductionAssets);

	if (obj.src)
		return obj.src;

	// A theme-variable binding on the image source takes precedence over the object's
	// own field path, resolving the variable's image value from the theme lookup; the
	// object's own field path still resolves against the record.
	const varName = boundImageVarName(obj);
	if (varName)
		return fieldImageUrl(boundImageFieldPath(obj)!, resolveThemeVar(theme, varName), urls, proxyProductionAssets);

	return fieldImageUrl(obj.field, resolveField(record, obj.field), urls, proxyProductionAssets);
}

// Walk a dotted path (e.g. 'lineup.0.name') into a record, tolerating missing intermediates.
function resolveField(obj: unknown, path: string | undefined): unknown
{
	return path?.split('.').reduce((cur: unknown, key) => (cur as Record<string, unknown>)?.[key], obj);
}

// Resolve the display URL for a poster-uploaded image. Poster uploads store a single
// 'medium' version (svgs included); fall back to 'large'/'full' for images uploaded via
// the older shared upload path.
function uploadedImageUrl(upload: UploadedImageData, urls: Pick<Urls,'imageUrl'>, proxyProductionAssets: boolean): string | null
{
	const sizeKey = upload.formats.medium ? 'medium' : upload.formats.large ? 'large' : 'full';
	const fmt = upload.formats[sizeKey];
	if (fmt)
		return urls.imageUrl(upload.folder, upload.shared, upload.hash, sizeKey, fmt.format, upload.dev, proxyProductionAssets);
	return null;
}
